import { useState, useEffect } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Dialog,
  IconButton,
  Skeleton,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import { PermissionGate, Permissions } from '@billda/permissions-ui';
import { useVariationImages } from '../../../hooks/useVariationImages';
import type { VariationImageResponse } from '../../../types/variationImage';
import {
  getCachedUrl,
  cacheImage,
} from '../../../services/variationImageCache';

interface VariationImageGalleryProps {
  variationRequestId: string;
  minimal?: boolean;
}

export function VariationImageGallery({
  variationRequestId,
  minimal,
}: VariationImageGalleryProps) {
  return (
    <PermissionGate permission={Permissions.Labour.VariationImages.Read}>
      <VariationImageGalleryContent
        variationRequestId={variationRequestId}
        minimal={minimal}
      />
    </PermissionGate>
  );
}

interface PreviewMedia {
  url: string;
  contentType: string;
}

function isVideo(contentType: string) {
  return contentType.startsWith('video/');
}

function VariationImageGalleryContent({
  variationRequestId,
  minimal,
}: VariationImageGalleryProps) {
  const {
    data: images,
    isLoading,
    error,
  } = useVariationImages(variationRequestId);
  const [preview, setPreview] = useState<PreviewMedia | null>(null);
  const [resolvedUrls, setResolvedUrls] = useState<Map<string, string>>(
    new Map()
  );

  const allImages = images ?? [];
  const uploadedImages = allImages.filter(img => img.status === 'Uploaded');
  const pendingCount = allImages.filter(img => img.status === 'Pending').length;

  // Resolve and cache blob URLs for uploaded images
  useEffect(() => {
    if (uploadedImages.length === 0) return;

    let cancelled = false;

    const resolve = async () => {
      const updates = new Map<string, string>();

      for (const img of uploadedImages) {
        if (!img.url) continue;
        const cached = getCachedUrl(img.contentHash);
        if (cached) {
          updates.set(img.id, cached);
        } else {
          try {
            const url = await cacheImage(img.contentHash, img.url);
            if (cancelled) return;
            updates.set(img.id, url);
          } catch {
            // Fall back to SAS URL if caching fails
            updates.set(img.id, img.url);
          }
        }
      }

      if (!cancelled) {
        setResolvedUrls(prev => {
          const next = new Map(prev);
          for (const [k, v] of updates) next.set(k, v);
          return next;
        });
      }
    };

    resolve();
    return () => {
      cancelled = true;
    };
    // Re-run when the image list changes (new uploads or refetch)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadedImages.map(img => img.contentHash).join(',')]);

  if (isLoading) {
    const grid = (
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: 1.5,
        }}
        data-testid="variation-images-loading"
      >
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton
            key={i}
            variant="rectangular"
            sx={{ aspectRatio: '1', borderRadius: 1 }}
          />
        ))}
      </Box>
    );
    if (minimal) return grid;
    return (
      <Card variant="outlined">
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Photos
          </Typography>
          {grid}
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Alert severity="error" data-testid="variation-images-error">
        Failed to load variation images
      </Alert>
    );
  }

  if (allImages.length === 0) {
    return null;
  }

  const handlePreview = (img: VariationImageResponse) => {
    const url = resolvedUrls.get(img.id) ?? img.url;
    if (url) setPreview({ url, contentType: img.contentType });
  };

  const grid = (
    <Box
      data-testid="variation-images-gallery"
      sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1.5 }}
    >
      {uploadedImages.map(img => {
        const displayUrl = resolvedUrls.get(img.id) ?? img.url;
        const video = isVideo(img.contentType);

        return (
          <Box
            key={img.id}
            component="button"
            type="button"
            aria-label={`Preview ${img.originalFileName}`}
            data-testid={`variation-image-thumbnail-${img.id}`}
            onClick={() => handlePreview(img)}
            disabled={!displayUrl}
            sx={{
              p: 0,
              border: 'none',
              background: 'none',
              cursor: displayUrl ? 'pointer' : 'default',
              borderRadius: 1,
              overflow: 'hidden',
              position: 'relative',
              '&:hover img, &:hover video': { opacity: 0.85 },
            }}
          >
            {!displayUrl ? (
              <Skeleton
                variant="rectangular"
                sx={{ width: '100%', aspectRatio: '1' }}
              />
            ) : video ? (
              <>
                <Box
                  component="video"
                  src={displayUrl}
                  muted
                  sx={{
                    display: 'block',
                    width: '100%',
                    aspectRatio: '1',
                    objectFit: 'cover',
                  }}
                />
                <PlayCircleOutlineIcon
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    color: 'common.white',
                    fontSize: 36,
                    filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.6))',
                    pointerEvents: 'none',
                  }}
                />
              </>
            ) : (
              <Box
                component="img"
                src={displayUrl}
                alt={img.originalFileName}
                sx={{
                  display: 'block',
                  width: '100%',
                  aspectRatio: '1',
                  objectFit: 'cover',
                }}
              />
            )}
          </Box>
        );
      })}

      {/* Pending image placeholders */}
      {Array.from({ length: pendingCount }).map((_, i) => (
        <Box
          key={`pending-${i}`}
          data-testid="variation-image-pending"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            aspectRatio: '1',
            borderRadius: 1,
            bgcolor: 'action.hover',
            gap: 0.5,
          }}
        >
          <HourglassEmptyIcon sx={{ fontSize: 20, color: 'text.disabled' }} />
          <Typography
            variant="caption"
            color="text.disabled"
            sx={{ textAlign: 'center', px: 0.5, lineHeight: 1.2 }}
          >
            Awaiting upload
          </Typography>
        </Box>
      ))}
    </Box>
  );

  return (
    <>
      {minimal ? (
        grid
      ) : (
        <Card variant="outlined">
          <CardContent>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 2,
              }}
            >
              <Typography variant="h6">Photos</Typography>
              <Typography variant="body2" color="text.secondary">
                {allImages.length}
              </Typography>
            </Box>
            {grid}
          </CardContent>
        </Card>
      )}

      <Dialog
        open={!!preview}
        onClose={() => setPreview(null)}
        maxWidth={false}
        slotProps={{
          paper: {
            sx: {
              bgcolor: 'common.black',
              m: 1,
              maxHeight: '90vh',
              maxWidth: '90vw',
            },
          },
        }}
      >
        {preview && (
          <>
            <IconButton
              onClick={() => setPreview(null)}
              aria-label="Close preview"
              data-testid="variation-image-preview-close"
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                color: 'common.white',
                bgcolor: 'rgba(0,0,0,0.5)',
                zIndex: 1,
                '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' },
              }}
            >
              <CloseIcon />
            </IconButton>
            {isVideo(preview.contentType) ? (
              <Box
                component="video"
                src={preview.url}
                controls
                autoPlay
                sx={{
                  display: 'block',
                  maxWidth: '100%',
                  maxHeight: '85vh',
                }}
              />
            ) : (
              <Box
                component="img"
                src={preview.url}
                alt="Preview"
                sx={{
                  display: 'block',
                  maxWidth: '100%',
                  maxHeight: '85vh',
                  objectFit: 'contain',
                }}
              />
            )}
          </>
        )}
      </Dialog>
    </>
  );
}
